import { useCallback, useEffect, useRef, useState } from "react";

interface UseContainerWidthOptions {
    breakpoint?: number;
    className?: string;
}

/**
 * Hook that monitors container width and applies a CSS class when below breakpoint
 * @param options Configuration options
 * @returns ref to attach to the element and current width state
 */
const useContainerWidth = <T extends HTMLElement = HTMLDivElement>(
    options: UseContainerWidthOptions = {}
) => {
    const { breakpoint = 750, className = "-stacked" } = options;
    const ref = useRef<T>(null);
    const [width, setWidth] = useState<number>(0);
    const [isNarrow, setIsNarrow] = useState<boolean>(false);

    const updateWidth = useCallback(() => {
        if (ref.current) {
            const newWidth = ref.current.offsetWidth;
            setWidth(newWidth);

            const newIsNarrow = newWidth <= breakpoint;
            setIsNarrow(newIsNarrow);

            requestAnimationFrame(() => {
                if (ref.current) {
                    if (newIsNarrow) {
                        ref.current.classList.add(className);
                    } else {
                        ref.current.classList.remove(className);
                    }
                }
            });
        }
    }, [breakpoint, className]);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        updateWidth();

        // Use ResizeObserver if available
        if (window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(updateWidth);
            resizeObserver.observe(element);

            return () => {
                resizeObserver.disconnect();
            };
        }

        // Fallback to window resize event
        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, [updateWidth]);

    return {
        ref,
        width,
        isNarrow,
    };
};

export default useContainerWidth;
